
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { MessageSquare, X, Send, Minimize, Maximize } from "lucide-react";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: Date;
}

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      content: "Hi there! I'm Agent V. How can I help you with your podcast today?",
      sender: "assistant",
      timestamp: new Date(),
    },
  ]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      if (messages.length === 1) {
        inputRef.current?.focus();
      }
    }
  }, [messages, isOpen, isMinimized]);
  
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    setIsMinimized(false);
  };
  
  const minimizeChat = () => {
    setIsMinimized(true);
  };
  
  const maximizeChat = () => {
    setIsMinimized(false);
  };
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      sender: "user",
      timestamp: new Date(),
    };
    
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    
    // Simulate AI response
    setTimeout(() => {
      let responseContent = "I'm still learning how to process that request. In the future, I'll be able to help schedule guests, update settings, and more.";
      
      // Simple keyword detection for demo purposes
      if (input.toLowerCase().includes("schedule") || input.toLowerCase().includes("guest")) {
        responseContent = "I understand you want to schedule a guest. In the future, I'll help you set that up directly from this chat.";
      } else if (input.toLowerCase().includes("setting") || input.toLowerCase().includes("profile")) {
        responseContent = "I can help you update your podcast settings. In the future, you'll be able to make those changes directly here.";
      }
      
      const aiMessage: Message = {
        id: Date.now().toString(),
        content: responseContent,
        sender: "assistant",
        timestamp: new Date(),
      };
      
      setMessages((prev) => [...prev, aiMessage]);
    }, 1000);
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Closed state - just the button */}
      {!isOpen && (
        <Button
          onClick={toggleChat}
          className="h-12 w-12 rounded-full shadow-lg"
          size="icon"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}
      
      {/* Open state - full chat interface */}
      {isOpen && (
        <Card className={cn(
          "transition-all duration-200 w-80 shadow-lg flex flex-col",
          isMinimized ? "h-14" : "h-96"
        )}>
          <CardHeader className="px-4 py-2 border-b flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2 text-primary" />
              <h3 className="text-sm font-medium">Agent V Chat</h3>
            </div>
            <div className="flex items-center space-x-1">
              {isMinimized ? (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7" 
                  onClick={maximizeChat}
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7" 
                  onClick={minimizeChat}
                >
                  <Minimize className="h-4 w-4" />
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7" 
                onClick={toggleChat}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          
          {!isMinimized && (
            <>
              <CardContent className="p-4 overflow-y-auto flex-grow">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex",
                        message.sender === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      <div
                        className={cn(
                          "rounded-lg px-3 py-2 max-w-[85%]",
                          message.sender === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        )}
                      >
                        <p className="text-sm break-words">{message.content}</p>
                        <span className="text-xs opacity-70 block mt-1">
                          {message.timestamp.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </CardContent>
              
              <CardFooter className="p-2 border-t">
                <form className="flex w-full gap-2" onSubmit={handleSendMessage}>
                  <Input
                    ref={inputRef}
                    placeholder="Type a message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1"
                  />
                  <Button size="icon" type="submit" disabled={!input.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </CardFooter>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default ChatWidget;
